import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiImage, FiTrash2, FiUpload } from 'react-icons/fi';

const ACCEPTED_TYPES = ['image/png', 'image/jpeg'];

const ScreenshotUploadSlot = ({ id, title, description, file, maxFiles = 1, onChange, onRemove }) => {
  const inputRef = useRef(null);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [error, setError] = useState('');
  const files = useMemo(() => (Array.isArray(file) ? file : file ? [file] : []), [file]);
  const isMultiFile = maxFiles > 1;
  const hasFiles = files.length > 0;
  const canAddMore = files.length < maxFiles;

  useEffect(() => {
    if (!files.length) {
      setPreviewUrls([]);
      return undefined;
    }

    const nextUrls = files.map((nextFile) => URL.createObjectURL(nextFile));
    setPreviewUrls(nextUrls);
    return () => nextUrls.forEach((url) => URL.revokeObjectURL(url));
  }, [files]);

  const applyFiles = (nextFiles) => {
    setError('');
    const validFiles = Array.from(nextFiles || []);
    if (!validFiles.length) return;
    if (validFiles.some((nextFile) => !ACCEPTED_TYPES.includes(nextFile.type))) {
      setError('Upload a JPG or PNG screenshot.');
      return;
    }

    if (!isMultiFile) {
      onChange(validFiles[0]);
      return;
    }

    const nextSelection = [...files, ...validFiles].slice(0, maxFiles);
    onChange(nextSelection);
  };

  const handleInputChange = (event) => {
    applyFiles(event.target.files);
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    applyFiles(event.dataTransfer.files);
  };

  const removeFile = (index) => {
    setError('');
    if (!isMultiFile) {
      onRemove();
      return;
    }
    onChange(files.filter((_, fileIndex) => fileIndex !== index));
  };

  return (
    <div
      className={`ocr-upload-slot ${hasFiles ? 'has-file' : ''}`}
      onDragOver={(event) => event.preventDefault()}
      onDrop={handleDrop}
    >
      <div className="ocr-upload-slot-meta">
        <span className="ocr-upload-icon"><FiImage size={18} /></span>
        <div>
          <h4>{title}</h4>
          <p>{description}</p>
          {isMultiFile && <small className="ocr-upload-limit">Up to {maxFiles} screenshots</small>}
        </div>
      </div>

      {hasFiles && (
        <div className="ocr-upload-preview-list">
          {files.map((selectedFile, index) => (
            <div className="ocr-upload-preview" key={`${selectedFile.name}-${selectedFile.lastModified}-${index}`}>
              <img src={previewUrls[index]} alt={`${title} ${index + 1}`} />
              <div className="ocr-upload-file-meta">
                <span>{selectedFile.name}</span>
                <small>{Math.max(1, Math.round(selectedFile.size / 1024))} KB</small>
              </div>
              {isMultiFile && (
                <button type="button" className="ocr-remove-file-btn" onClick={() => removeFile(index)}>
                  <FiTrash2 size={14} />
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {canAddMore && (
        <button
          type="button"
          className="ocr-upload-dropzone"
          onClick={() => inputRef.current?.click()}
        >
          <FiUpload size={18} />
          <span>{hasFiles ? 'Add Screenshot' : 'Upload Screenshot'}</span>
        </button>
      )}

      {!isMultiFile && hasFiles && (
        <div className="ocr-upload-actions">
          <button type="button" onClick={() => inputRef.current?.click()}>
            Replace
          </button>
          <button type="button" className="danger" onClick={onRemove}>
            <FiTrash2 size={14} />
            Remove
          </button>
        </div>
      )}

      {isMultiFile && hasFiles && !canAddMore && (
        <div className="ocr-upload-actions ocr-upload-actions--note">
          <span className="ocr-upload-max-note">Maximum reached</span>
        </div>
      )}

      {error && <p className="ocr-upload-error">{error}</p>}

      <input
        ref={inputRef}
        id={id}
        type="file"
        accept="image/png,image/jpeg"
        multiple={isMultiFile}
        onChange={handleInputChange}
        hidden
      />
    </div>
  );
};

export default ScreenshotUploadSlot;
